import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styles from "./index.module.scss";
import { message, Button, Modal, Form, Input, Select } from "antd";
import {
  get_fiatPaymentMethodUserList,
  get_fiatListForWithdraw,
  get_fiatPaymentMethodListForWithdraw,
  post_fiatConfigureUserPaymentMethod,
  delete_fiatUserPaymentMethod,
} from "@/api/xt4/payment";
import { waitRes } from "utils/axios";
import { Hooks } from "@xt/xtbase";
import store from "store";
import DeleteIcon from "@/assets/images/delete.png";

const { useTranslation } = Hooks;
const { Option } = Select;

interface PaymentMethodItem {
  groupKey: string;
  payName: string;
  realName: string;
  status: number;
  icon?: string;
  accountNumber?: string;
  createdTime: string;
  updatedTime: string;
}

interface StandardField {
  key: string;
  name: string;
  isMust: number;
  sort?: number;
}

/** list/withdraw 下单个渠道（用于 VeePay/Bcpay/PIX 与 fields 兜底） */
interface PaymentMethodRoute {
  payName?: string;
  payEnName?: string;
  fields?: PaymentMethodRouteField[];
  configs?: PaymentMethodRouteField[];
}

interface PaymentMethodGroupOption {
  groupKey: string;
  payName: string;
  icon?: string;
  standardFields?: StandardField[];
  paymentMethods?: PaymentMethodRoute[];
  /** 接口：是否已在本组绑过卡 */
  isConfigured?: boolean;
  currencyId?: number;
  minAmount?: string;
  maxAmount?: string;
}

/** 接口 list/withdraw 组项内嵌渠道的 fields（与组级 standardFields 二选一或并存） */
interface PaymentMethodRouteField {
  columnName?: string;
  columnEnName?: string;
  isMust?: number;
  sort?: number;
}

interface FiatCurrency {
  /** 与接口对齐：优先 currencyId，兼容历史 id */
  id: number;
  currency: string;
  displayName: string;
}

function normalizeFiatCurrencyRow(item: Record<string, unknown>): FiatCurrency | null {
  const rawId = item.currencyId ?? item.id;
  const id = typeof rawId === "number" ? rawId : rawId != null ? Number(rawId) : NaN;
  if (Number.isNaN(id)) return null;
  const cur = (item.currency ?? item.coinName ?? "") as string;
  const displayName =
    (item.displayName as string) ||
    (item.currencyName as string) ||
    (typeof cur === "string" && cur ? cur.toUpperCase() : "") ||
    String(id);
  return {
    id,
    currency: typeof cur === "string" ? cur : String(cur),
    displayName,
  };
}

/** 从组项解析 standardFields：优先组级 standardFields，否则用 paymentMethods[0].fields */
function resolveStandardFieldsFromGroupItem(item: Record<string, unknown>): StandardField[] {
  const top = item.standardFields;
  if (Array.isArray(top) && top.length > 0) {
    return top as StandardField[];
  }
  const pms = item.paymentMethods;
  if (!Array.isArray(pms) || pms.length === 0) return [];
  const first = pms[0] as Record<string, unknown>;
  const rawFields = first.fields;
  if (!Array.isArray(rawFields) || rawFields.length === 0) return [];
  return (rawFields as PaymentMethodRouteField[]).map((f) => ({
    key: (f.columnEnName || f.columnName || "") as string,
    name: (f.columnName || f.columnEnName || "") as string,
    isMust: typeof f.isMust === "number" ? f.isMust : 0,
    sort: f.sort,
  }));
}

function getGroupDisplayWeight(item: Record<string, unknown>): number {
  const pms = Array.isArray(item.paymentMethods) ? item.paymentMethods : [];
  if (pms.length === 0) return 999;
  const w = (pms[0] as Record<string, unknown>)?.displayWeights;
  return typeof w === "number" ? w : 999;
}

function normalizeWithdrawGroupItem(item: Record<string, unknown>): PaymentMethodGroupOption | null {
  const groupKey = item.groupKey;
  if (!groupKey || typeof groupKey !== "string") return null;
  const standardFields = resolveStandardFieldsFromGroupItem(item);
  const pms = item.paymentMethods;
  return {
    groupKey,
    payName: (item.payName as string) || "",
    icon: item.icon as string | undefined,
    standardFields,
    paymentMethods: Array.isArray(pms) ? (pms as PaymentMethodRoute[]) : [],
    isConfigured: item.isConfigured === true,
    currencyId: typeof item.currencyId === "number" ? item.currencyId : undefined,
    minAmount: item.minAmount != null ? String(item.minAmount) : undefined,
    maxAmount: item.maxAmount != null ? String(item.maxAmount) : undefined,
  };
}

function isVeePayMethod(method: PaymentMethodRoute | null | undefined): boolean {
  if (!method) return false;
  const name = (method.payEnName || method.payName || "").toLowerCase();
  return name === "veepay" || name.includes("veepay");
}

function isBcpayMethod(method: PaymentMethodRoute | null | undefined): boolean {
  if (!method) return false;
  const name = (method.payEnName || method.payName || "").toLowerCase();
  return name === "bcpay" || name.includes("bcpay");
}

function isPixMethod(method: PaymentMethodRoute | null | undefined): boolean {
  if (!method) return false;
  const name = (method.payEnName || method.payName || "").toLowerCase();
  return name === "pix" || name.includes("pix");
}

function isPixDualBindingGroup(groupMethod: PaymentMethodGroupOption | null): boolean {
  if (!groupMethod) return false;
  const groupName = (groupMethod.payName || "").toLowerCase();
  if (!groupName.includes("pix")) return false;
  const methods = Array.isArray(groupMethod.paymentMethods) ? groupMethod.paymentMethods : [];
  return methods.some((m) => isVeePayMethod(m)) && methods.some((m) => isBcpayMethod(m));
}

/** PIX 渠道：bankCode 固定 CPF，不展示 CNPJ/PHONE/EMAIL（与 web-index 法币提现绑卡一致） */
function isPixWithdrawGroup(groupMethod: PaymentMethodGroupOption | null): boolean {
  if (!groupMethod) return false;
  if (isPixDualBindingGroup(groupMethod)) return true;
  const groupName = (groupMethod.payName || "").toLowerCase();
  if (groupName.includes("pix")) return true;
  const methods = Array.isArray(groupMethod.paymentMethods) ? groupMethod.paymentMethods : [];
  return methods.some((m) => isPixMethod(m));
}

function getMethodConfigs(method: PaymentMethodRoute | null | undefined): PaymentMethodRouteField[] {
  if (!method) return [];
  const m = method as Record<string, unknown>;
  if (Array.isArray(m.configs) && m.configs.length > 0) return m.configs as PaymentMethodRouteField[];
  if (Array.isArray(m.fields) && m.fields.length > 0) return m.fields as PaymentMethodRouteField[];
  return [];
}

function isAccountFieldKey(key: string | undefined): boolean {
  const k = String(key || "").toLowerCase();
  return k === "account";
}

function isCpfFieldKey(key: string | undefined): boolean {
  const k = String(key || "").toLowerCase();
  return k === "cpf" || k === "document";
}

/** Account 与 CPF 同值提交时，弹窗不展示 Account 输入框 */
function shouldHideAccountField(fields: StandardField[]): boolean {
  return fields.some((f) => isAccountFieldKey(f.key)) && fields.some((f) => isCpfFieldKey(f.key));
}

const PaymentMethod: React.FC = () => {
  const t = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodItem[]>([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [fiatCurrencies, setFiatCurrencies] = useState<FiatCurrency[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<PaymentMethodGroupOption[]>([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState<number | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [standardFields, setStandardFields] = useState<StandardField[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingItem, setDeletingItem] = useState<PaymentMethodItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** 与 web-index 提现绑卡一致：无 standardFields 时 VeePay/Bcpay/PIX 双通道定制表单 */
  const [isVeePayForm, setIsVeePayForm] = useState(false);
  const [isBcpayForm, setIsBcpayForm] = useState(false);
  const [veePayForm, setVeePayForm] = useState({ bankCode: "", accountNo: "", accountName: "", document: "" });
  const [bcpayForm, setBcpayForm] = useState({ bankCode: "", accountNo: "", accountName: "", document: "" });

  const BANK_CODE_OPTIONS = [
    { value: "CPF", label: "CPF" },
    { value: "CNPJ", label: "CNPJ" },
    { value: "PHONE", label: "PHONE" },
    { value: "EMAIL", label: "EMAIL" },
  ];

  const selectedGroupForModal = useMemo(
    () => (selectedGroupKey ? paymentMethodOptions.find((g) => g.groupKey === selectedGroupKey) ?? null : null),
    [selectedGroupKey, paymentMethodOptions]
  );
  const isPixWithdrawContext = useMemo(() => isPixWithdrawGroup(selectedGroupForModal), [selectedGroupForModal]);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const { data, err } = await waitRes(get_fiatPaymentMethodUserList, {
        params: {
          status: 1,
        },
      });
      if (err) {
        const errorMsg =
          typeof err === "string"
            ? err
            : (typeof err === "object" && err !== null && "message" in err ? (err as any).message : null) || t("paymentMethod.failedToLoad");
        message.error(errorMsg);
        return;
      }
      const result = data?.result || data?.data || data;
      if (Array.isArray(result)) {
        setPaymentMethods(result);
      } else if (result?.items) {
        setPaymentMethods(result.items);
      } else if (result?.list) {
        setPaymentMethods(result.list);
      } else {
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error(t("paymentMethod.errorLoadLog"), error);
      message.error(t("paymentMethod.failedToLoad"));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleAdd = async () => {
    await fetchPaymentMethods({ silent: true });
    setAddModalVisible(true);
    await fetchFiatList();
  };

  const fetchFiatList = async () => {
    try {
      const { data, err } = await waitRes(get_fiatListForWithdraw, {
        params: {
          pageIndex: 1,
          pageSize: 100,
        },
      });
      if (err) return;
      const result = data?.result || data?.data || data;
      let items: FiatCurrency[] = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result?.items) {
        items = result.items;
      } else if (result?.list) {
        items = result.list;
      }
      const filtered = items
        .filter((item: any) => item.withdrawStatus === 1 && item.isDisplay === 1)
        .map((item: any) => normalizeFiatCurrencyRow(item as Record<string, unknown>))
        .filter((c): c is FiatCurrency => c != null);
      setFiatCurrencies(filtered);
    } catch (error) {
      console.error(t("paymentMethod.errorFetchFiatLog"), error);
    }
  };

  const handleCurrencyChange = async (currencyId: number) => {
    setSelectedCurrencyId(currencyId);
    setSelectedGroupKey(null);
    setStandardFields([]);
    setIsVeePayForm(false);
    setIsBcpayForm(false);
    setVeePayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
    setBcpayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
    // resetFields 会清空 currencyId，需写回以保持 Select 展示与所选一致
    form.resetFields();
    form.setFieldsValue({ currencyId });
    await fetchPaymentMethodOptions(currencyId);
  };

  const fetchPaymentMethodOptions = async (currencyId: number) => {
    try {
      const { data, err } = await waitRes(get_fiatPaymentMethodListForWithdraw, {
        params: {
          currencyId,
          pageIndex: 1,
          pageSize: 100,
          includeConfigs: true,
        },
      });
      if (err) return;
      const result = data?.result || data?.data || data;
      let items: any[] = [];
      if (Array.isArray(result)) {
        items = result;
      } else if (result?.items) {
        items = result.items;
      } else if (result?.list) {
        items = result.list;
      }
      // 与 web-index 提现页一致：组级 normalize + displayWeights 排序（不在此过滤已绑定，改在选支付方式时拦截）
      const zipped = items
        .filter((item: any) => !!item?.groupKey)
        .map((item: any) => {
          const raw = item as Record<string, unknown>;
          const opt = normalizeWithdrawGroupItem(raw);
          if (!opt) return null;
          return { opt, w: getGroupDisplayWeight(raw) };
        })
        .filter((x): x is { opt: PaymentMethodGroupOption; w: number } => x != null);
      zipped.sort((a, b) => a.w - b.w);
      setPaymentMethodOptions(zipped.map((z) => z.opt));
    } catch (error) {
      console.error(t("paymentMethod.errorFetchPaymentMethodLog"), error);
    }
  };

  /** 与 web-index buildFormFieldsFromStandardFields 一致 */
  const handlePaymentMethodChange = (groupKey: string | undefined) => {
    if (!groupKey) {
      setSelectedGroupKey(null);
      setStandardFields([]);
      setIsVeePayForm(false);
      setIsBcpayForm(false);
      setVeePayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
      setBcpayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
      const fv = form.getFieldsValue();
      const cleared: Record<string, unknown> = {};
      Object.keys(fv).forEach((k) => {
        if (k.startsWith("field_")) cleared[k] = undefined;
      });
      form.setFieldsValue(cleared);
      return;
    }
    if (paymentMethods.some((p) => p.groupKey === groupKey)) {
      message.error(t("paymentMethod.alreadyBound"));
      form.setFieldsValue({ groupKey: undefined });
      setSelectedGroupKey(null);
      setStandardFields([]);
      setIsVeePayForm(false);
      setIsBcpayForm(false);
      setVeePayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
      setBcpayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
      return;
    }
    setSelectedGroupKey(groupKey);
    const fv = form.getFieldsValue();
    const cleared: Record<string, unknown> = {};
    Object.keys(fv).forEach((k) => {
      if (k.startsWith("field_")) cleared[k] = undefined;
    });
    form.setFieldsValue(cleared);

    const group = paymentMethodOptions.find((item) => item.groupKey === groupKey) || null;
    if (!group) {
      setStandardFields([]);
      setIsVeePayForm(false);
      setIsBcpayForm(false);
      return;
    }

    const fromTop = Array.isArray(group.standardFields) ? group.standardFields : [];
    const sortedTop = [...fromTop].sort((a, b) => (a.sort || 999) - (b.sort || 999));
    if (sortedTop.length > 0) {
      setStandardFields(sortedTop);
      setIsVeePayForm(false);
      setIsBcpayForm(false);
      if (isPixWithdrawGroup(group)) {
        const bcField = sortedTop.find((f) => String(f.key || "").toLowerCase() === "bankcode");
        if (bcField) {
          form.setFieldsValue({ [`field_${bcField.key}`]: "CPF" });
        }
      }
      return;
    }

    if (isPixDualBindingGroup(group)) {
      setStandardFields([]);
      setIsVeePayForm(true);
      setIsBcpayForm(false);
      setVeePayForm({ bankCode: "CPF", accountNo: "", accountName: "", document: "" });
      return;
    }

    const pm = group.paymentMethods?.[0];
    if (isVeePayMethod(pm)) {
      setStandardFields([]);
      setIsVeePayForm(true);
      setIsBcpayForm(false);
      setVeePayForm({ bankCode: isPixWithdrawGroup(group) ? "CPF" : "", accountNo: "", accountName: "", document: "" });
      return;
    }
    if (isBcpayMethod(pm)) {
      setStandardFields([]);
      setIsVeePayForm(false);
      setIsBcpayForm(true);
      setBcpayForm({ bankCode: isPixWithdrawGroup(group) ? "CPF" : "", accountNo: "", accountName: "", document: "" });
      return;
    }

    const configs = getMethodConfigs(pm);
    if (configs.length > 0) {
      const mapped: StandardField[] = configs.map((config) => ({
        key: (config.columnEnName || config.columnName || "") as string,
        name: (config.columnName || config.columnEnName || "") as string,
        isMust: typeof config.isMust === "number" ? config.isMust : 0,
        sort: config.sort,
      }));
      const sorted = [...mapped].sort((a, b) => (a.sort || 999) - (b.sort || 999));
      setStandardFields(sorted);
      setIsVeePayForm(false);
      setIsBcpayForm(false);
      if (isPixWithdrawGroup(group)) {
        const bcField = sorted.find((f) => String(f.key || "").toLowerCase() === "bankcode");
        if (bcField) {
          form.setFieldsValue({ [`field_${bcField.key}`]: "CPF" });
        }
      }
      return;
    }

    setStandardFields([]);
    setIsVeePayForm(false);
    setIsBcpayForm(false);
  };

  const handleModalCancel = () => {
    setAddModalVisible(false);
    setSelectedCurrencyId(null);
    setSelectedGroupKey(null);
    setStandardFields([]);
    setPaymentMethodOptions([]);
    setIsVeePayForm(false);
    setIsBcpayForm(false);
    setVeePayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
    setBcpayForm({ bankCode: "", accountNo: "", accountName: "", document: "" });
    form.resetFields();
  };

  const handleModalSubmit = async () => {
    try {
      await form.validateFields(["currencyId", "groupKey"]);
      if (!selectedGroupKey) {
        message.error(t("paymentMethod.pleaseSelectPaymentMethod"));
        return;
      }
      if (paymentMethods.some((p) => p.groupKey === selectedGroupKey)) {
        message.error(t("paymentMethod.alreadyBound"));
        return;
      }

      const group = paymentMethodOptions.find((g) => g.groupKey === selectedGroupKey);
      if (!group) {
        message.error(t("paymentMethod.pleaseSelectPaymentMethod"));
        return;
      }

      const stdForRequired =
        Array.isArray(group.standardFields) && group.standardFields.length > 0
          ? group.standardFields
          : resolveStandardFieldsFromGroupItem(group as unknown as Record<string, unknown>);
      const requiredKeys = stdForRequired.filter((f) => f.isMust === 1).map((f) => f.key).filter(Boolean);

      const toTrim = (v: unknown) => (v == null ? "" : String(v)).trim();
      let fields: Record<string, string> = {};

      if (isVeePayForm || isBcpayForm) {
        const formSource = isVeePayForm ? veePayForm : bcpayForm;
        let bankCode = toTrim(formSource.bankCode);
        if (!bankCode && isPixWithdrawContext) bankCode = "CPF";
        const accountNo = toTrim(formSource.accountNo);
        const accountName = toTrim(formSource.accountName);
        const document = toTrim(formSource.document);
        const lcKeys = stdForRequired.map((x) => (x.key || "").toLowerCase());
        const findKey = (...cands: string[]) => {
          const hit = cands.find((k) => lcKeys.includes(k));
          if (!hit) return null;
          return stdForRequired.find((x) => (x.key || "").toLowerCase() === hit)?.key || null;
        };
        const accountNameKey = findKey("accountname", "realname", "name");
        const accountKey = findKey("account", "accountno", "accountnumber");
        const documentKey = findKey("document", "cpf", "iban");
        const bankCodeKey = findKey("bankcode", "type");
        const accountValue = isPixWithdrawContext && document ? document : accountNo;
        if (accountNameKey && accountName) fields[accountNameKey] = accountName;
        if (accountKey && accountValue) fields[accountKey] = accountValue;
        if (documentKey && document) fields[documentKey] = document;
        if (bankCodeKey && bankCode) fields[bankCodeKey] = bankCode;
        if (!accountNameKey && accountName) fields.accountName = accountName;
        if (!accountKey && accountValue) fields.account = accountValue;
        if (!documentKey && document) fields.document = document;
        if (!bankCodeKey && bankCode) fields.bankCode = bankCode;
      } else {
        const values = await form.validateFields();
        const hideAccount = shouldHideAccountField(standardFields);
        const cpfField = standardFields.find((f) => isCpfFieldKey(f.key));
        const cpfValue = cpfField?.key ? toTrim(values[`field_${cpfField.key}`]) : "";
        const accountField = standardFields.find((f) => isAccountFieldKey(f.key));
        for (const f of standardFields) {
          const k = f.key;
          if (!k) continue;
          if (hideAccount && isAccountFieldKey(k)) continue;
          let v = toTrim(values[`field_${k}`]);
          if (String(k).toLowerCase() === "bankcode" && isPixWithdrawContext) v = v || "CPF";
          if (v !== "") fields[k] = v;
        }
        if (hideAccount && accountField?.key && cpfValue) {
          fields[accountField.key] = cpfValue;
        }
      }

      const missingRequired = requiredKeys.filter((k) => !fields[k] || toTrim(fields[k]) === "");
      if (missingRequired.length > 0) {
        message.warning(t("paymentMethod.pleaseFillRequiredFields"));
        return;
      }
      if (Object.keys(fields).length === 0) {
        message.warning(t("paymentMethod.pleaseFillAtLeastOneField"));
        return;
      }

      setSubmitting(true);
      // 与 web-index 一致：realName 传 userId，由后端按用户资料处理
      const realName = String(store.user.userInfo?.userId ?? "");

      const { err } = await waitRes(post_fiatConfigureUserPaymentMethod, {
        data: {
          groupKey: selectedGroupKey,
          realName,
          fields,
        },
      });

      if (err) {
        const errorMsg =
          typeof err === "string"
            ? err
            : (typeof err === "object" && err !== null && "message" in err ? (err as any).message : null) || t("paymentMethod.failedToAdd");
        message.error(errorMsg);
        return;
      }

      message.success(t("paymentMethod.addSuccess"));
      handleModalCancel();
      await fetchPaymentMethods();
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(t("paymentMethod.errorAddLog"), error);
      message.error(t("paymentMethod.failedToAdd"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (item: PaymentMethodItem) => {
    setDeletingItem(item);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem?.groupKey) return;

    try {
      setDeleting(true);
      const { err, data: errData } = await waitRes((config) => delete_fiatUserPaymentMethod(deletingItem.groupKey, config), {});

      if (err) {
        let errorMsg = t("paymentMethod.failedToDelete");
        if (errData && typeof errData === "object" && "mc" in errData && errData.mc) {
          errorMsg = t(errData.mc as string, (errData as { ma?: string[] }).ma);
        } else if (typeof err === "string" && err) {
          errorMsg = err;
        } else if (typeof err === "object" && err !== null && "message" in err && (err as { message?: string }).message) {
          errorMsg = (err as { message: string }).message;
        }
        message.error(errorMsg);
        return;
      }

      message.success(t("paymentMethod.deleteSuccess"));
      setDeleteModalVisible(false);
      setDeletingItem(null);
      await fetchPaymentMethods();
    } catch (error) {
      console.error(t("paymentMethod.errorDeleteLog"), error);
      message.error(t("paymentMethod.failedToDelete"));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeletingItem(null);
  };

  const maskAccountNumber = (value: string): string => {
    if (!value || typeof value !== "string") return "****";
    const len = value.length;
    if (len <= 1) return "*";
    if (len === 2) return "**";
    if (len === 3) return value[0] + "*" + value[2];
    if (len === 4) return value[0] + "**" + value[3];
    if (len === 5) return value.slice(0, 2) + "*" + value.slice(-2);
    if (len === 6) return value.slice(0, 2) + "**" + value.slice(-2);
    return value.slice(0, 3) + "*".repeat(len - 6) + value.slice(-3);
  };

  const formatAccountNumber = (item: PaymentMethodItem) => {
    if (item.accountNumber) return maskAccountNumber(item.accountNumber);
    return "****";
  };

  return (
    <div className={styles["payment-method-wrapper"]}>
      <div className={styles["content"]}>
        <div className={styles["description"]}>{t("paymentMethod.description")}</div>

        <Button type="primary" className={styles["add-btn"]} onClick={handleAdd} block>
          {t("paymentMethod.addAddress")}
        </Button>

        <div className={styles["payment-list"]}>
          {loading ? (
            <div className={styles["loading"]}>{t("paymentMethod.loading")}</div>
          ) : paymentMethods.length === 0 ? (
            <div className={styles["empty"]}>{t("paymentMethod.empty")}</div>
          ) : (
            paymentMethods.map((item) => (
              <div key={item.groupKey} className={styles["payment-item"]}>
                <div className={styles["payment-info"]}>
                  <div className={styles["payment-header"]}>
                    {item.icon && <img className={styles["payment-icon"]} src={item.icon} alt={item.payName} />}
                    <div className={styles["payment-name"]}>{item.payName || t("paymentMethod.paymentMethod")}</div>
                  </div>
                  <div className={styles["payment-account"]}>
                    <span className={styles["account-label"]}>{t("paymentMethod.accountNumber")}</span>
                    <span className={styles["account-number"]}>{formatAccountNumber(item)}</span>
                  </div>
                </div>
                <div className={styles["delete-btn"]} onClick={() => handleDelete(item)}>
                  🗑️
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        open={addModalVisible}
        title={t("paymentMethod.addTitle")}
        onCancel={handleModalCancel}
        footer={null}
        className={styles["add-payment-modal"]}
        width={400}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleModalSubmit}>
          <Form.Item
            label={t("paymentMethod.currency")}
            name="currencyId"
            rules={[{ required: true, message: t("paymentMethod.pleaseSelectCurrency") }]}
          >
            <Select
              placeholder={t("paymentMethod.selectCurrency")}
              onChange={handleCurrencyChange}
              showSearch
              filterOption={(input, option) => {
                if (!option) return false;
                const label = option.label as unknown;
                const children = option.children as unknown;
                const labelStr = typeof label === "string" ? label : "";
                const childrenStr = typeof children === "string" ? children : "";
                const text = labelStr || childrenStr || "";
                return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            >
              {fiatCurrencies.map((currency) => (
                <Option key={currency.id} value={currency.id}>
                  {currency.displayName || currency.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedCurrencyId && (
            <Form.Item
              label={t("paymentMethod.paymentMethod")}
              name="groupKey"
              rules={[{ required: true, message: t("paymentMethod.pleaseSelectPaymentMethod") }]}
            >
              <Select
                placeholder={t("paymentMethod.selectPaymentMethod")}
                onChange={handlePaymentMethodChange}
                showSearch
                filterOption={(input, option) => {
                  if (!option) return false;
                  const label = typeof option.label === "string" ? option.label : "";
                  const children = typeof option.children === "string" ? option.children : "";
                  const text = label || children || "";
                  return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
              >
                {paymentMethodOptions.map((method) => (
                  <Option key={method.groupKey} value={method.groupKey}>
                    {method.payName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {selectedGroupKey && (isVeePayForm || isBcpayForm) && (
            <>
              {!isPixWithdrawContext && (
                <Form.Item label="bankCode" required>
                  <Select
                    placeholder={`${t("paymentMethod.enterValue", { value: "bankCode" })}`}
                    value={(isVeePayForm ? veePayForm : bcpayForm).bankCode}
                    onChange={(v) =>
                      isVeePayForm ? setVeePayForm((s) => ({ ...s, bankCode: v })) : setBcpayForm((s) => ({ ...s, bankCode: v }))
                    }
                  >
                    {BANK_CODE_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
              {!isPixWithdrawContext && (
                <Form.Item label="accountNo" required>
                  <Input
                    autoComplete="off"
                    placeholder={t("paymentMethod.enterValue", { value: "accountNo" })}
                    value={(isVeePayForm ? veePayForm : bcpayForm).accountNo}
                    onChange={(e) =>
                      isVeePayForm
                        ? setVeePayForm((s) => ({ ...s, accountNo: e.target.value }))
                        : setBcpayForm((s) => ({ ...s, accountNo: e.target.value }))
                    }
                  />
                </Form.Item>
              )}
              <Form.Item label="accountName" required>
                <Input
                  autoComplete="off"
                  placeholder={t("paymentMethod.enterValue", { value: "accountName" })}
                  value={(isVeePayForm ? veePayForm : bcpayForm).accountName}
                  onChange={(e) =>
                    isVeePayForm
                      ? setVeePayForm((s) => ({ ...s, accountName: e.target.value }))
                      : setBcpayForm((s) => ({ ...s, accountName: e.target.value }))
                  }
                />
              </Form.Item>
              <Form.Item label="CPF" required>
                <Input
                  autoComplete="off"
                  maxLength={14}
                  placeholder={t("paymentMethod.enterValue", { value: "CPF" })}
                  value={(isVeePayForm ? veePayForm : bcpayForm).document}
                  onChange={(e) =>
                    isVeePayForm
                      ? setVeePayForm((s) => ({ ...s, document: e.target.value }))
                      : setBcpayForm((s) => ({ ...s, document: e.target.value }))
                  }
                />
              </Form.Item>
            </>
          )}

          {selectedGroupKey && !isVeePayForm && !isBcpayForm && standardFields.length > 0 && (
            <>
              {standardFields
                .filter((field) => !(shouldHideAccountField(standardFields) && isAccountFieldKey(field.key)))
                .map((field) => (
                <Form.Item
                  key={field.key}
                  label={field.name || field.key}
                  name={`field_${field.key}`}
                  rules={field.isMust === 1 ? [{ required: true, message: t("paymentMethod.pleaseEnterField", { field: field.name || field.key }) }] : []}
                >
                  {String(field.key || "").toLowerCase() === "bankcode" ? (
                    <Select placeholder={t("paymentMethod.enterValue", { value: "bankCode" })}>
                      {(isPixWithdrawContext ? BANK_CODE_OPTIONS.filter((o) => o.value === "CPF") : BANK_CODE_OPTIONS).map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Input placeholder={t("paymentMethod.enterValue", { value: field.name || field.key })} />
                  )}
                </Form.Item>
              ))}
            </>
          )}

          {selectedGroupKey && !isVeePayForm && !isBcpayForm && standardFields.length === 0 && (
            <div className={styles["loading-form-hint"]}>{t("paymentMethod.loadingFormFields")}</div>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting} className={styles["submit-btn"]}>
              {t("paymentMethod.confirm")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={deleteModalVisible}
        title={null}
        onCancel={handleDeleteCancel}
        footer={null}
        className={styles["delete-payment-modal"]}
        width={320}
        centered
        closable
        maskClosable
      >
        <div className={styles["delete-modal-content"]}>
          <div className={styles["delete-modal-icon"]}>
            <img src={DeleteIcon} alt="delete" />
          </div>
          <div className={styles["delete-modal-title"]}>{t("paymentMethod.deleteCard")}</div>
          <div className={styles["delete-modal-message"]}>
            {deletingItem &&
              t("paymentMethod.deleteConfirmMessage", {
                name: deletingItem.payName || t("paymentMethod.paymentMethod"),
                number: formatAccountNumber(deletingItem),
              })}
          </div>
          <div className={styles["delete-modal-actions"]}>
            <Button className={styles["delete-cancel-btn"]} onClick={handleDeleteCancel} disabled={deleting}>
              {t("paymentMethod.cancel")}
            </Button>
            <Button className={styles["delete-confirm-btn"]} onClick={handleDeleteConfirm} loading={deleting}>
              {t("paymentMethod.confirm")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default observer(PaymentMethod);
